
<script setup>

  import { computed } from 'vue';

  import { useRouter } from 'vue-router';

  import { ArrowLeftCircleIcon, ArrowRightCircleIcon } from '@heroicons/vue/24/outline';

  import TextArea from '../components/text-area.vue';

  import SaveNotice from '../partials/save-notice.vue';

  import { selected, selections } from '../stores/activity-4.js';

  import { formatTimelineDate } from '../utils.js';

  import backgroundPath from '../images/children-background.jpg';

  const router = useRouter();

  if ( selections.value < 3 ) router.replace( { path: '/activity/4/part/1' } );

  const props = defineProps( { id: Number } );

  const item = computed( () => selected.value[ props.id - 1 ] );

  const backPath = computed( () => ( [ '/activity/4/part/1', '/activity/4/part/2/exercise/1', '/activity/4/part/2/exercise/2' ] )[ props.id - 1 ] );

  const continuePath = computed( () => ( [ '/activity/4/part/2/exercise/2', '/activity/4/part/2/exercise/3', '/activity/4/review' ] )[ props.id - 1 ] );
  
</script>

<template>

  <article>

    <div class="grid items-center min-h-view bg-fixed bg-cover bg-center"
    
      :style="{ backgroundImage: `url( ${ backgroundPath } )` }">

      <div class="p-6 md:p-12 lg:p-24">

        <div class="max-w-xl mx-auto">

          <label :for="`activity-4-part-2-response-${ item.id }`" class="text-4xl md:text-5xl font-heading uppercase">
            
            Analyze This Event
          
          </label>

          <p class="mt-4 leading-7 text-lg text-neutral-700">

            From the
            
            <RouterLink to="/activity/4/part/1" class="underline">Timeline</RouterLink>,
            
            you chose three events that were the impetuses for change. Now, analyze each event.

          </p>
          
          <div class="flex divide-x-2 divide-neutral-300 mt-12 border-2 border-neutral-300">

            <RouterLink v-for="number in 3"

              :class="[ 'grow px-2 py-3 text-center font-bold hover:underline focus-visible:underline', { 'bg-amber-300': number === id } ]"
            
              :to="`/activity/4/part/2/exercise/${ number }`">

              <span class="md:hidden">Evt.</span>

              <span class="hidden md:inline">Event</span>
              
              {{ number }}
            
            </RouterLink>

          </div>

          <div class="mt-12 px-6 py-7 rounded-sm bg-neutral-200">

            <div class="text-xl font-semibold">{{ formatTimelineDate( item ) }}</div>

            <div class="mt-2 text-4xl font-heading">{{ item.title }}</div>

            <div class="mt-4">{{ item.description }}</div>

          </div>

          <p class="mt-12 text-neutral-700">Here are some suggested questions to get you started:</p>

          <ul class="mt-8 pl-8 space-y-2 list-disc text-neutral-700">

            <li>Who was the key person (or people) involved?</li>

            <li>What action or event initiated change?</li>

            <li>Was this a positive or negative change?</li>

            <li>What was it like before and after the change?</li>

            <li>Are the results or effects of the change still around today?</li>

          </ul>

          <TextArea class="mt-12" :id="`activity-4-part-2-response-${ item.id }`" :maxLength="1400" v-model="item.response" v-slot="{ count, maxLength }">
            
            <div class="text-center mt-2 font-bold text-lg text-neutral-500 italic">{{ count }} of {{ maxLength }} characters used.</div>
          
          </TextArea>

          <SaveNotice class="mt-14" />

          <div class="flex flex-wrap justify-between gap-6 max-w-xl mt-12">

            <RouterLink

              class="inline-flex items-center gap-4 px-6 py-4 text-white font-bold text-white bg-neutral-500 transition-colors
              
                hover:bg-neutral-600 focus-visible:bg-neutral-800 hover:underline focus-visible:underline"
            
              :to="backPath">
              
              <ArrowLeftCircleIcon class="w-8" />

              <span>Back</span>
            
            </RouterLink>

            <RouterLink

              class="inline-flex items-center gap-4 px-6 py-4 text-white font-bold text-white bg-green-700 transition-colors
              
                hover:bg-green-800 focus-visible:bg-green-800 hover:underline focus-visible:underline"
            
              :to="continuePath">
              
              <span>Continue</span>

              <ArrowRightCircleIcon class="w-8" />
            
            </RouterLink>

          </div>

        </div>

      </div>

    </div>

  </article>

</template>
